/**
 * Deep linking utilities for redirecting to mobile app if installed.
 */

import axiosInstance from '@/lib/axios';

export type Platform = 'ios' | 'android' | 'unknown';

export interface PlatformAvailability {
  iosAvailable: boolean;
  androidAvailable: boolean;
}

/**
 * Get the deep link scheme for a given hostname.
 * @param hostname The hostname of the current page (e.g., 'unchaai.milora.org')
 * @returns The scheme (e.g., 'unchaai') or null if no mapping exists.
 */
export function getSchemeForHost(hostname: string): string | null {
  const normalizedHost = normalizeHostname(hostname);

  const specialSchemes: Record<string, string> = {};
  if (specialSchemes[normalizedHost]) {
    return specialSchemes[normalizedHost];
  }

  if (normalizedHost === 'milora.org' || normalizedHost.endsWith('.milora.org')) {
    const parts = normalizedHost.split('.');
    return parts[0];
  }

  const parts = normalizedHost.split('.');
  if (parts.length >= 2) {
    return parts[0];
  }

  return null;
}

function normalizeHostname(hostname: string): string {
  if (hostname === 'www.milora.org') {
    return 'milora.org';
  }
  return hostname;
}

/**
 * Get the tenant slug from hostname.
 * @param hostname The hostname (e.g., 'unchaai.milora.org')
 * @returns The slug (e.g., 'unchaai') or null.
 */
export function getSlugFromHostname(hostname: string): string | null {
  const normalizedHost = normalizeHostname(hostname);

  if (normalizedHost === 'milora.org') {
    return 'milora';
  }
  if (normalizedHost.endsWith('.milora.org')) {
    const parts = normalizedHost.split('.');
    if (parts.length >= 3) {
      return parts[0];
    }
  }
  return null;
}

/**
 * Detect the current platform.
 */
export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'unknown';
  const userAgent = navigator.userAgent || navigator.vendor;
  const ua = userAgent.toLowerCase();

  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'unknown';
}

/**
 * Check if the current device is mobile (iOS or Android).
 */
export function isMobileDevice(): boolean {
  return detectPlatform() !== 'unknown';
}

/**
 * Fetch platform availability from API.
 */
export async function fetchPlatformAvailability(
  slug: string
): Promise<PlatformAvailability | null> {
  try {
    const { data } = await axiosInstance.get<PlatformAvailability>(
      '/api/mobile-apps/platform-check',
      { params: { slug } }
    );
    return data;
  } catch {
    return null;
  }
}

/**
 * Attempt to open the native app via deep link.
 * @param deepLinkUrl The full deep link URL (e.g., 'milora://example.com/events/123')
 * @param fallbackUrl The web URL to stay on if app is not installed.
 * @returns A promise that resolves to true if deep link was triggered, false otherwise.
 */
export function attemptDeepLink(deepLinkUrl: string, _fallbackUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    let didBlur = false;

    // Listen for blur event (app opened)
    const handleBlur = () => {
      didBlur = true;
      window.removeEventListener('blur', handleBlur);
      resolve(true);
    };
    window.addEventListener('blur', handleBlur);

    // Set a timeout to detect failure
    const timeoutId = setTimeout(() => {
      window.removeEventListener('blur', handleBlur);
      if (!didBlur) {
        // App not opened, stay on web
        resolve(false);
      }
    }, 2000);

    // Attempt to navigate to deep link
    window.location.href = deepLinkUrl;

    // Also listen for page visibility change (user returns to page)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timeoutId);
        window.removeEventListener('visibilitychange', handleVisibilityChange);
        resolve(false); // User returned, assume deep link failed
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });
}

/**
 * Main function to redirect to mobile app if installed.
 * Should be called on page load for mobile devices.
 * Only attempts deep link if the current platform has a published app.
 * @param currentUrl The current web URL (window.location.href)
 * @returns A promise that resolves to true if redirected to app, false otherwise.
 */
export async function redirectToAppIfInstalled(currentUrl: string): Promise<boolean> {
  const platform = detectPlatform();
  if (platform === 'unknown') {
    return false;
  }

  try {
    const url = new URL(currentUrl);
    const scheme = getSchemeForHost(url.hostname);
    const slug = getSlugFromHostname(url.hostname);

    if (!scheme || !slug) {
      return false;
    }

    // Check if this platform has a published app
    const availability = await fetchPlatformAvailability(slug);

    if (!availability) {
      // Can't check availability - skip to avoid failed deep link attempt
      return false;
    }

    const hasAppForPlatform =
      platform === 'ios' ? availability.iosAvailable : availability.androidAvailable;

    if (!hasAppForPlatform) {
      // No app for this platform - stay on web
      return false;
    }

    // Construct deep link: scheme://hostname/path?search
    const deepLinkUrl = `${scheme}://${url.hostname}${url.pathname}${url.search}`;

    const redirected = await attemptDeepLink(deepLinkUrl, currentUrl);
    return redirected;
  } catch (error) {
    console.error('Deep link error:', error);
    return false;
  }
}
